// Whether a tenant of a confirmed settlement has paid. A payment request
// linked to the settlement decides: while it is open the tenant has not
// paid, whatever the broker marked by hand (the server's delete guard reads
// the request the same way). Only without a linked request does the manual
// "paid" mark count, for a tenant who paid outside Rentio.

import {
  Invoice,
  isTenantPaidStatus,
  Settlement,
  SettlementAction,
} from "./types";

type TPaymentLinks = Pick<
  SettlementAction,
  "paymentOrderId" | "paymentRequestId" | "manualPaidAt"
>;

// A mark that says money changed hands outside a Rentio payment: the
// tenant marked paid by hand, or a refund marked as paid back (refundedAt,
// "mark as refunded" in the confirmed view; decision D6 of 2026-10-15).
const hasPaidMark = (
  action: TPaymentLinks & { refundedAt?: string | null },
): boolean => !!action.manualPaidAt || !!action.refundedAt;

// Whether a tenant's refund is done: marked as paid back (refundedAt, from
// "mark as refunded" in the confirmed view) or by hand. Until then every
// refund is still to do.
export const isSettlementRefundDone = (
  action: TPaymentLinks & { refundedAt?: string | null },
): boolean => hasPaidMark(action);

// The day a refund was marked as paid back, when it was.
export const getSettlementRefundDoneAt = (
  action: TPaymentLinks & { refundedAt?: string | null },
): string | undefined => action.refundedAt ?? action.manualPaidAt ?? undefined;

export const hasLinkedPayment = (action: TPaymentLinks): boolean =>
  action.paymentOrderId != null || action.paymentRequestId != null;

export const isSettlementActionPaid = (params: {
  action: TPaymentLinks;
  requestStatus?: string;
}): boolean => {
  const { action, requestStatus } = params;
  return hasLinkedPayment(action)
    ? isTenantPaidStatus(requestStatus)
    : !!action.manualPaidAt;
};

export const getSettlementActionPaidAt = (params: {
  action: TPaymentLinks;
  requestPaidAt?: string;
}): string | undefined => {
  const { action, requestPaidAt } = params;
  return hasLinkedPayment(action) ? requestPaidAt : action.manualPaidAt;
};

// A linked request that is not paid (or whose status is not known yet).
export const isSettlementActionOpen = (params: {
  action: TPaymentLinks;
  requestStatus?: string;
}): boolean =>
  hasLinkedPayment(params.action) && !isTenantPaidStatus(params.requestStatus);

// An invitation whose payment was withdrawn under Payments is no invitation
// any more: the tenant is outstanding again, can be invited again or marked
// paid, and nothing open blocks deleting the settlement. The server drops
// such a link itself (SyncSettlementOnPaymentDeletedHandler); this also
// covers a link withdrawn before that sync existed, or whose sync failed.
// Withdrawn = the API counts its order among the ended ones (deleted or
// completed, with no live request of the tenant left, see
// useEndedPaymentOrderIds), and the user sees no live request either: a live
// request always decides, as the order of a paid payment can end too.
export const withoutWithdrawnLink = <Action extends TPaymentLinks>(params: {
  action: Action;
  requestStatus?: string;
  endedOrderIds?: ReadonlySet<number>;
}): Action => {
  const { action, requestStatus, endedOrderIds } = params;
  const isWithdrawn =
    requestStatus === undefined &&
    action.paymentOrderId != null &&
    !!endedOrderIds?.has(Number(action.paymentOrderId));
  return isWithdrawn
    ? { ...action, paymentOrderId: undefined, paymentRequestId: undefined }
    : action;
};

// The live payment status per payment order and per request, and the linked
// orders that ended (see usePaymentStatusMaps in hooks).
export interface IActionStatusMaps<Status> {
  byOrderId: Record<number, Status>;
  byRequestId: Record<number, Status>;
  endedOrderIds?: ReadonlySet<number>;
}

// A tenant's payment status: their own request (bulk mode shares one order
// across tenants), else the order for older snapshots that only stored the
// order id.
export const getActionPaymentStatus = <Status>(params: {
  action: TPaymentLinks;
  statusMaps: IActionStatusMaps<Status>;
}): Status | undefined => {
  const { action, statusMaps } = params;
  return (
    (action.paymentRequestId != null
      ? statusMaps.byRequestId[action.paymentRequestId]
      : undefined) ??
    (action.paymentOrderId != null
      ? statusMaps.byOrderId[action.paymentOrderId]
      : undefined)
  );
};

// A settlement's actions without the links of withdrawn payments.
export const getLiveSettlementActions = <
  Status extends { status: string },
>(params: {
  actions: SettlementAction[];
  statusMaps: IActionStatusMaps<Status>;
}): SettlementAction[] => {
  const { actions, statusMaps } = params;
  return actions.map(action =>
    withoutWithdrawnLink({
      action,
      requestStatus: getActionPaymentStatus({ action, statusMaps })?.status,
      endedOrderIds: statusMaps.endedOrderIds,
    }),
  );
};

// A tenant payment of a settlement that is still open, with what names it to
// the broker: the tenant as the settlement froze them, and for a group
// settlement their unit (its label in snapshot.groupUnits, else the frozen
// unitCode when that says more than the tenant's name).
export interface ISettlementOpenPayment {
  contractId: string;
  tenantName?: string;
  unitLabel?: string;
}

// The open payments of a settlement, on its live actions (a withdrawn link
// is no payment any more, see getLiveSettlementActions); an unknown status
// counts as open (see isSettlementActionOpen).
export const settlementOpenPayments = <
  Status extends { status: string },
>(params: {
  settlement: Pick<Settlement, "snapshot">;
  statusMaps: IActionStatusMaps<Status>;
}): ISettlementOpenPayment[] => {
  const { settlement, statusMaps } = params;
  const perContract = settlement.snapshot?.perContract ?? [];
  const groupUnits = settlement.snapshot?.groupUnits ?? [];
  return getLiveSettlementActions({
    actions: settlement.snapshot?.actions ?? [],
    statusMaps,
  })
    .filter(action =>
      isSettlementActionOpen({
        action,
        requestStatus: getActionPaymentStatus({ action, statusMaps })?.status,
      }),
    )
    .map(action => {
      const row = perContract.find(pc => pc.contractId === action.contractId);
      const tenantName = row?.tenantName?.trim() || undefined;
      const frozenCode = row?.unitCode?.trim();
      const unitLabel =
        groupUnits.find(unit => String(unit.id) === String(row?.unitId))
          ?.label ??
        (frozenCode && frozenCode !== tenantName ? frozenCode : undefined);
      return {
        contractId: action.contractId,
        ...(tenantName ? { tenantName } : {}),
        ...(unitLabel ? { unitLabel } : {}),
      };
    });
};

// "Hugo (Street 7 box 2), Lewis (Street 7 box 1)": the open
// payments as a refusal lists them, each once; one without a tenant name goes
// by its unit.
export const openPaymentsList = (payments: ISettlementOpenPayment[]): string =>
  [
    ...new Set(
      payments.flatMap(payment => {
        if (payment.tenantName && payment.unitLabel) {
          return [`${payment.tenantName} (${payment.unitLabel})`];
        }
        const label = payment.tenantName ?? payment.unitLabel;
        return label ? [label] : [];
      }),
    ),
  ].join(", ");

// The tenants of a settlement with a payment, by the names it froze
// (snapshot.perContract), each once; a tenant it froze without a name is
// `unnamed`. A payment is a paid linked request, a tenant marked paid by
// hand, or a refund marked as paid back. A settlement with a payment is never
// deleted (decision of 2026-10-06); the server refuses it the same way
// (rentio-api-next settlementDeleteById). Read on the live actions, so a
// withdrawn payment does not count and a mark by hand next to it does.
export const settlementPaidTenants = <
  Status extends { status: string },
>(params: {
  settlement: Pick<Settlement, "snapshot">;
  statusMaps: IActionStatusMaps<Status>;
}): { names: string[]; hasUnnamed: boolean } => {
  const { settlement, statusMaps } = params;
  const perContract = settlement.snapshot?.perContract ?? [];
  const paid = getLiveSettlementActions({
    actions: settlement.snapshot?.actions ?? [],
    statusMaps,
  }).filter(
    action =>
      hasPaidMark(action) ||
      isSettlementActionPaid({
        action,
        requestStatus: getActionPaymentStatus({ action, statusMaps })?.status,
      }),
  );
  const names = paid.map(
    action =>
      perContract
        .find(row => row.contractId === action.contractId)
        ?.tenantName?.trim() || undefined,
  );
  return {
    names: [...new Set(names.filter((name): name is string => !!name))],
    hasUnnamed: names.some(name => !name),
  };
};

// The properties whose payment requests a list reads for the live status of
// its settlements and costs: the given ones, plus each settlement's own
// property and the units it froze for its tenants, plus the unit each cost
// split billed. A tenant's request lives on their own unit, which stays so
// once that unit is archived and gone from the office's property list.
export const paymentRequestPropertyIds = (params: {
  propertyIds: (string | number | undefined)[];
  settlements?: Pick<Settlement, "unitId" | "groupId" | "snapshot">[];
  costs?: Pick<Invoice, "paymentSplits">[];
}): number[] => {
  const { propertyIds, settlements = [], costs = [] } = params;
  const ids = [
    ...propertyIds,
    ...settlements.flatMap(settlement => [
      settlement.unitId,
      settlement.groupId,
      ...(settlement.snapshot?.perContract ?? []).map(pc => pc.unitId),
    ]),
    ...costs.flatMap(cost =>
      (cost.paymentSplits ?? []).map(split => split.propertyId),
    ),
  ]
    .map(Number)
    .filter(id => Number.isInteger(id) && id > 0);
  return [...new Set(ids)].sort((a, b) => a - b);
};
